/*
 * Компоненты Infra
 *
 * nova - инстансы ВМ
 * cinder - вольюмы
 * neutron network
 * keystone - авториция пользователя (используется во всех сервисах)
 * horizon - дашборд
 * panko - логгер о действиях инфровой апишки
 * barbican - хранилище ключей
 * octavia - балансеры
 * glance - образы для инстансов
 * heat - оркестрация ресурсов
 * manila - файловое хранилище
 * magnum - кубернетис
 * trove - базы данных
 * karbor - бэкапы автоматические и ручные
 *
 */

import * as R from 'ramda';
import { CommonModule } from 'utils/CommonModule';
import { reduxHelper } from 'utils/reduxHelper';

import { PermissionApi } from 'reactApp/api/PermissionApi';
import { GlobalState } from 'reactApp/core/rootReducer';
import * as PermissionTypes from 'reactApp/modules/PermissionsModule/PermissionModule.types';

type InfraServicesNamesMap = {
    [key in PermissionTypes.InfraPermissionName]: PermissionTypes.InfraPermissionName;
};
export const INFRA_SERVICES: InfraServicesNamesMap = {
    aodh: 'aodh',
    barbican: 'barbican',
    ceilometer: 'ceilometer',
    cinder: 'cinder',
    cloudkitty: 'cloudkitty',
    freezer: 'freezer',
    glance: 'glance',
    gnocchi: 'gnocchi',
    heat: 'heat',
    karbor: 'karbor',
    keystone: 'keystone',
    magnum: 'magnum',
    manila: 'manila',
    murano: 'murano',
    neutron: 'neutron',
    nova: 'nova',
    octavia: 'octavia',
    panko: 'panko',
    sahara: 'sahara',
    trove: 'trove',
    vpn: 'vpn',
    horizon_cinder: 'horizon_cinder',
    horizon_glance: 'horizon_glance',
    horizon_heat: 'horizon_heat',
    horizon_keystone: 'horizon_keystone',
    horizon_neutron: 'horizon_neutron',
    horizon_nova: 'horizon_nova',
};

type McsServicesNamesMap = {
    [key in PermissionTypes.McsPermissionName]: PermissionTypes.McsPermissionName;
};
export const MCS_SERVICES: McsServicesNamesMap = {
    // network: 'network', // api send it but we should not use it, use neutron instead
    hotbox: 'hotbox',
    vision: 'vision',
    billing: 'billing',
    accounts: 'accounts',
    marketplace: 'marketplace',
    infra: 'infra',
    project: 'project',
    gpu: 'gpu',
    databases: 'databases',
    containers: 'containers',
    bigdata: 'bigdata',
};

const {
    module,
    Actions: { loadData },
    Selectors: { getData, getHasError, getIsLoaded, getIsLoading },
} = CommonModule.createCommonModule<PermissionTypes.AllPermissions>({
    initialData: {} as any,
    dataFieldKey: 'permissions',
    apiMethods: PermissionApi,
    entityName: 'permissions',
    entityNamePublic: 'доступы',
});

const getHasNamesPermission = ({
    permissions,
    names,
    permission,
}: {
    names: PermissionTypes.PermissionName[];
    permissions: PermissionTypes.AllPermissions;
    permission: PermissionTypes.PermissionPostfix;
}) => names.every((name) => permissions[`${name}_${permission}`]);

const isModifiable = (
    names: PermissionTypes.PermissionName[],
    permissions: PermissionTypes.AllPermissions,
) => getHasNamesPermission({ names, permissions, permission: 'modify' });
const isViewable = (
    names: PermissionTypes.PermissionName[],
    permissions: PermissionTypes.AllPermissions,
) => getHasNamesPermission({ names, permissions, permission: 'view' });
const isDeletable = (
    names: PermissionTypes.PermissionName[],
    permissions: PermissionTypes.AllPermissions,
) => getHasNamesPermission({ names, permissions, permission: 'delete' });

const getModuleState = module.getModuleState;

type CheckingPermissions = {
    modify?: PermissionTypes.PermissionName[];
    view?: PermissionTypes.PermissionName[];
    del?: PermissionTypes.PermissionName[];
};
const checkPermissions = R.curry(
    (
        { modify = [], view = [], del = [] }: CheckingPermissions,
        state: GlobalState,
    ) => {
        const permissions = getData(state);
        return (
            isModifiable(modify, permissions) &&
            isViewable(view, permissions) &&
            isDeletable(del, permissions)
        );
    },
);

export const PermissionSelectors = {
    getModuleState,

    getPermissions: getData,

    getHasError,
    getIsLoaded,
    getIsLoading,

    checkPermissions,
};

// @ts-ignore
const loadPermissions = (pid: string) => loadData(pid);

export const PermissionsActions = {
    loadPermissions,
};

export const BoundPermissionsActions = reduxHelper.bindActions<
    typeof PermissionsActions
>(PermissionsActions);
